// Mute / unmute notifications for a single task.
//
// Renders a bell-with-slash toggle. When muted, the user still receives
// rows in their notification history (so nothing is lost), but the unread
// badge and toast/push are suppressed.
import React, { useEffect, useState } from "react";
import { Bell, BellOff } from "lucide-react";
import { tasksApi } from "../api/tasks";

interface Props {
  taskId: string;
  compact?: boolean;
}

export function MuteTaskButton({ taskId, compact = false }: Props) {
  const [muted, setMuted] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    tasksApi
      .getTaskMute(taskId)
      .then(res => { if (!cancelled) setMuted(!!res.data?.muted); })
      .catch(() => { if (!cancelled) setMuted(false); });
    return () => { cancelled = true; };
  }, [taskId]);

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    const prev = muted;
    setMuted(!prev); // optimistic
    try {
      const res = await tasksApi.toggleTaskMute(taskId);
      setMuted(!!res.data?.muted);
    } catch {
      setMuted(prev); // roll back
    } finally {
      setBusy(false);
    }
  };

  const isMuted = !!muted;
  const label = isMuted ? "Unmute notifications" : "Mute notifications";

  if (compact) {
    return (
      <button
        onClick={toggle}
        disabled={busy || muted === null}
        title={label}
        aria-label={label}
        className={`p-2 rounded-lg transition-colors ${
          isMuted
            ? "bg-rose-50 text-rose-600 hover:bg-rose-100"
            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
        } disabled:opacity-50`}
      >
        {isMuted ? <BellOff size={16} /> : <Bell size={16} />}
      </button>
    );
  }

  return (
    <button
      onClick={toggle}
      disabled={busy || muted === null}
      title={label}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
        isMuted
          ? "bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200"
          : "bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200"
      } disabled:opacity-50`}
    >
      {isMuted ? <BellOff size={14} /> : <Bell size={14} />}
      {isMuted ? "Muted" : "Mute"}
    </button>
  );
}
